import { Box, Details, Heading } from "../../../../../../mads";
import { HtmlContent } from "../../../../../../shared/components/HtmlContent";
import { useViewJobDataContext } from "../../../../context/ViewJobDataContext";

import { colors } from "@indeed/ifl-tokens";
import { fontSizes, lineHeights, spacing } from "@indeed/ifl-tokens/react-native";
import React from "react";
import { StyleSheet } from "react-native";

const CONTENT_MARGIN = 16;

interface FaqSectionContentProps {
  section: component.faqfootprint.FaqSection;
  sectionIndex: number;
  isLast: boolean;
}

const ANSWER_CLASS = "faq-footprint-answer";
const DISCLAIMER_CLASS = "faq-footprint-disclaimer";

// Scope each rule by class so the FAQ's CSS can't leak across other
// HtmlContent instances rendered on the same page.
// `line-height: 19px` matches the legacy SEO FAQ design and is intentionally
// non-tokenized — it sits between IFL `lineHeights.tight` (17.5px) and `normal`
// (21px), neither of which matches the visual reference.
const ANSWER_INJECTED_STYLES = `
    .${ANSWER_CLASS} { font-size: ${fontSizes.f2}px; line-height: 19px; }
    .${ANSWER_CLASS} > :last-child { margin-bottom: 0; }
    .${ANSWER_CLASS} em { font-size: ${fontSizes.f1}px; }
    .${ANSWER_CLASS} a { color: ${colors.primary["800"]}; }
`;

// Applied on the block wrapper rather than `em`: an inline child cannot shrink
// the line box below the block's strut, so a smaller line-height set on `em`
// would be silently ignored under the inherited 24px.
const DISCLAIMER_INJECTED_STYLES = `
    .${DISCLAIMER_CLASS} {
        font-size: ${fontSizes.f1}px;
        margin-top: ${spacing.s3}px;
        line-height: ${lineHeights.tight};
    }
`;

const FaqSectionContent: React.FC<FaqSectionContentProps> = ({ section, sectionIndex, isLast }) => (
  <Box sx={!isLast ? styles.sectionWithBorder : undefined}>
    <Heading level={3} ariaLevel={4} sx={styles.sectionTitle}>
      {section.title}
    </Heading>
    {section.questions.map((item, questionIndex) => (
      <Box key={`${sectionIndex}-${questionIndex}`} sx={styles.questionContainer}>
        <Heading level={2} ariaLevel={5} sx={styles.questionText}>
          {item.question}
        </Heading>
        <HtmlContent
          testID={`faq-answer-${sectionIndex}-${questionIndex}`}
          className={ANSWER_CLASS}
          textColor={colors.neutral["1300"]}
          injectedStyles={ANSWER_INJECTED_STYLES}
        >
          {item.answer}
        </HtmlContent>
      </Box>
    ))}
  </Box>
);

export const FAQFootprint: React.FC = () => {
  const { initialData } = useViewJobDataContext();
  const faqModel = initialData?.llmSentimentFaqModel;

  if (!faqModel || faqModel.sections.length === 0) {
    return null;
  }

  return (
    <Box sx={styles.container}>
      <Details
        heading={
          <Heading level={3} sx={styles.headingText}>
            {faqModel.title}
          </Heading>
        }
        defaultOpen={false}
        sx={{ paddingHorizontal: CONTENT_MARGIN }}
      >
        <Box sx={styles.sectionsContainer}>
          {faqModel.sections.map((section, index) => (
            <FaqSectionContent
              key={`${index}-${section.title}`}
              section={section}
              sectionIndex={index}
              isLast={index === faqModel.sections.length - 1}
            />
          ))}
          {faqModel.disclaimer && (
            <HtmlContent
              testID="faq-disclaimer"
              className={DISCLAIMER_CLASS}
              textColor={colors.neutral["700"]}
              injectedStyles={DISCLAIMER_INJECTED_STYLES}
            >
              {faqModel.disclaimer}
            </HtmlContent>
          )}
        </Box>
      </Details>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    borderTopColor: colors.neutral["200"],
  },
  sectionsContainer: {
    paddingTop: 22,
  },
  sectionWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.neutral["200"],
    paddingBottom: 12,
    marginBottom: 12,
  },
  headingText: {
    marginBottom: 0,
  },
  sectionTitle: {
    color: colors.neutral["1000"],
    lineHeight: Math.round(fontSizes.f3 * 1.2),
    marginBottom: 0,
  },
  questionContainer: {
    marginBottom: 0,
  },
  questionText: {
    color: colors.neutral["800"],
    lineHeight: 19,
    marginTop: 12,
    marginBottom: 0,
  },
});
